import { db } from './db';
import type AuthUser from '~/types/authUser';
import { clearPrivateRuntimeCaches } from './authCache.mjs';

class ProfileSyncService {
  private isSyncing = false;

  async queueProfileUpdate(user: AuthUser) {
    if (!import.meta.client) return;

    const queueItem = {
      id: crypto.randomUUID(),
      type: 'update_profile' as const,
      payload: { user },
      status: 'pending' as const,
      retries: 0,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };

    // Remove any existing pending update_profile tasks to keep only the latest one
    const existingTasks = await db.syncQueue
      .where('type')
      .equals('update_profile')
      .filter((task) => task.status === 'pending' || task.status === 'error')
      .toArray();

    if (existingTasks.length > 0) {
      await db.syncQueue.bulkDelete(existingTasks.map((t) => t.id));
    }

    await db.syncQueue.add(queueItem);
  }

  async syncPending() {
    if (!import.meta.client || !navigator.onLine) return;
    if (this.isSyncing) return;

    this.isSyncing = true;
    try {
      const pendingTasks = await db.syncQueue
        .where('type')
        .equals('update_profile')
        .filter((t) => t.status === 'pending' || t.status === 'error')
        .toArray();

      // Sort to process oldest first (though we likely only have one due to queueProfileUpdate logic)
      pendingTasks.sort((a, b) => a.createdAt - b.createdAt);

      for (const task of pendingTasks) {
        try {
          await db.syncQueue.update(task.id, { status: 'syncing', updatedAt: Date.now() });

          const payload = task.payload as { user: AuthUser };
          const response = await $fetch('/api/users/authUser', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json; charset=utf-8',
              Accept: 'application/json; charset=utf-8'
            },
            body: { user: payload.user }
          });

          if (response) {
            await clearPrivateRuntimeCaches();
            await db.syncQueue.update(task.id, { status: 'synced', updatedAt: Date.now() });
            window.location.reload();
          } else {
            throw new Error('API returned empty response');
          }
        } catch (error) {
          console.error('Erro ao sincronizar perfil offline:', error);
          await db.syncQueue.update(task.id, {
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error',
            retries: task.retries + 1,
            updatedAt: Date.now()
          });
        }
      }
    } finally {
      this.isSyncing = false;
    }
  }
}

export const profileSyncService = new ProfileSyncService();
